import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { onboardingAPI } from '../services/api';
import { useAuth } from './AuthContext';
import { useLocation } from 'react-router-dom';

// Создаем контекст
const OnboardingContext = createContext();

// Хук для использования контекста
export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};

// Провайдер контекста
export const OnboardingProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  // Состояние онбординга
  const [isOnboardingActive, setIsOnboardingActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isProgressLoaded, setIsProgressLoaded] = useState(false);

  // Доступные гиды и текущий гид
  const [activeGuide, setActiveGuide] = useState(null);
  const [availableGuides, setAvailableGuides] = useState([]);
  const [currentUrl, setCurrentUrl] = useState('');
  const [currentTabId, setCurrentTabId] = useState('');

  // Прогресс пользователя
  const [userProgress, setUserProgress] = useState({});
  // Состояние дровера для онбординга
  const [shouldOpenDrawer, setShouldOpenDrawer] = useState(false);
  // Ссылка на методы Layout для управления дровером
  const [layoutMethods, setLayoutMethods] = useState(null);

  // --- Универсальный pageRef для любой страницы с онбордингом ---
  const pageRef = useRef(null);
  const setPageRef = (ref) => {
    pageRef.current = ref;
  };

  // Функция для получения методов Layout
  const setLayoutRef = useCallback((methods) => {
    setLayoutMethods(methods);
  }, []);

  // Функция для управления дровером через методы Layout
  const handleOnboardingDrawer = useCallback(
    (shouldOpen) => {
      console.log('handleOnboardingDrawer вызван:', shouldOpen);
      setShouldOpenDrawer(shouldOpen);

      // Если есть прямые методы управления дровером, используем их
      if (layoutMethods) {
        if (shouldOpen) {
          console.log('Вызываем layoutMethods.openDrawer()');
          layoutMethods.openDrawer();

          // Добавляем повторный вызов через таймаут для надежности
          setTimeout(() => {
            if (layoutMethods && !layoutMethods.isDrawerOpen()) {
              console.log('Повторно вызываем layoutMethods.openDrawer()');
              layoutMethods.openDrawer();
            }
          }, 300);
        } else {
          layoutMethods.closeDrawer();
        }
      } else {
        console.warn('layoutMethods не определены');
      }
    },
    [layoutMethods]
  );

  // Проверяет требуемое состояние UI для шага и управляет дровером
  const checkStepUIRequirements = useCallback(
    (step) => {
      if (!step) return;

      // Проверяем, требуется ли открытый дровер
      if (step.requiredUIState?.drawerOpen) {
        console.log(
          'checkStepUIRequirements: требуется открыть дровер для шага',
          step.order
        );

        // Устанавливаем флаг, что дровер должен быть открыт
        setShouldOpenDrawer(true);

        // Непосредственно открываем дровер через методы Layout
        if (layoutMethods) {
          console.log(
            'Вызываем layoutMethods.openDrawer() из checkStepUIRequirements'
          );
          layoutMethods.openDrawer();

          // На мобильных устройствах попробуем кликнуть по кнопке напрямую
          if (window.innerWidth < 600) {
            setTimeout(() => {
              // Если дровер все еще не открыт, пробуем кликнуть по кнопке
              if (!layoutMethods.isDrawerOpen()) {
                console.log('Пробуем кликнуть по кнопке дровера напрямую');
                const drawerButton =
                  document.querySelector('[data-testid="drawer-toggle"]') ||
                  document.querySelector('[aria-label="open drawer"]');

                if (drawerButton) {
                  drawerButton.click();
                }
              }
            }, 200);
          }
        } else {
          console.warn('layoutMethods не определены в checkStepUIRequirements');
        }
      } else {
        setShouldOpenDrawer(false);
        // Непосредственно закрываем дровер через методы Layout
        if (layoutMethods) {
          layoutMethods.closeDrawer();
        }
      }
    },
    [layoutMethods]
  );

  // Следим за изменением текущего шага для обновления состояния UI
  useEffect(() => {
    if (
      isOnboardingActive &&
      activeGuide &&
      activeGuide.steps &&
      currentStep >= 0
    ) {
      const step = activeGuide.steps[currentStep];
      if (step) {
        checkStepUIRequirements(step);
      }
    }
  }, [isOnboardingActive, activeGuide, currentStep, checkStepUIRequirements]);

  // Сбрасываем состояния UI при деактивации онбординга
  useEffect(() => {
    if (!isOnboardingActive) {
      setShouldOpenDrawer(false);
    }
  }, [isOnboardingActive]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.isElementVisible = function (selector) {
        const el = document.querySelector(selector);
        if (!el) return false;
        const style = window.getComputedStyle(el);
        if (
          style.display === 'none' ||
          style.visibility === 'hidden' ||
          style.opacity === '0'
        )
          return false;
        const rect = el.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) return false;
        return true;
      };

      return () => {
        delete window.isElementVisible; // Cleanup
      };
    }
  }, []);

  // Получение доступных гидов для пользователя
  const fetchAvailableGuides = useCallback(async () => {
    if (!isAuthenticated) {
      setIsLoading(false);
      return [];
    }

    try {
      setIsLoading(true);
      // Определяем текущий URL страницы
      const url = window.location.pathname;
      setCurrentUrl(url);

      // Получаем гиды для текущего URL
      const response = await onboardingAPI.getGuidesByUrl(url);
      const guides = response.data;

      setAvailableGuides(guides);
      setIsLoading(false);

      // Возвращаем полученные гиды для возможной дальнейшей обработки
      return guides;
    } catch (error) {
      console.error('Ошибка при получении гидов:', error);
      setIsLoading(false);
      return [];
    }
  }, [isAuthenticated]);

  // Получение гидов для вкладки
  const fetchGuidesByTab = useCallback(
    async (tabId) => {
      if (!isAuthenticated || !tabId) {
        return [];
      }

      try {
        setIsLoading(true);
        const url = window.location.pathname;
        setCurrentUrl(url);
        setCurrentTabId(tabId);

        // Получаем гиды для текущего URL и вкладки
        const response = await onboardingAPI.getGuidesByTab(url, tabId);
        const guides = response.data;

        setAvailableGuides(guides);
        setIsLoading(false);

        // Возвращаем полученные гиды для возможной дальнейшей обработки
        return guides;
      } catch (error) {
        console.error('Ошибка при получении гидов для вкладки:', error);
        setIsLoading(false);
        return [];
      }
    },
    [isAuthenticated]
  );

  // Проверка условий отображения для шага (универсальная)
  const checkStepDisplayConditions = useCallback((step) => {
    if (!step) return true;

    // 1. Проверка displayConditions.customCondition (JS строка)
    if (step.displayConditions && step.displayConditions.customCondition) {
      try {
        // eslint-disable-next-line no-new-func
        const condition = new Function(
          `return ${step.displayConditions.customCondition}`
        )();
        if (!condition) return false;
      } catch (error) {
        console.error(
          'Ошибка при проверке пользовательского условия отображения:',
          error
        );
        return false;
      }
    }

    // Если нет условий — показываем всегда
    return true;
  }, []);

  // Получение прогресса пользователя по конкретному гиду
  const fetchGuideProgress = useCallback(
    async (guideId) => {
      if (!isAuthenticated || !guideId) return null;

      try {
        console.log(`Запрашиваем прогресс для гида ${guideId}`);
        const response = await onboardingAPI.getGuideProgress(guideId);
        console.log(`Получен прогресс для гида ${guideId}:`, response.data);

        // Обновляем общий объект прогресса
        setUserProgress((prev) => {
          return {
            ...prev,
            [guideId]: {
              ...(response.data || {}),
              completed: response.data?.completed === true,
              guide: { _id: guideId },
            },
          };
        });

        return response.data;
      } catch (error) {
        console.error(
          `Ошибка при получении прогресса по гиду ${guideId}:`,
          error
        );
        return null;
      }
    },
    [isAuthenticated]
  );

  // Обновление прогресса на сервере
  const updateProgress = useCallback(
    async (guideId, step) => {
      if (!isAuthenticated || !guideId) return;

      try {
        console.log(`Обновляем прогресс для гида ${guideId}, шаг ${step}`);
        const response = await onboardingAPI.updateProgress(guideId, step);

        // Обновляем прогресс в состоянии
        setUserProgress((prev) => {
          const updatedProgress = {
            ...prev,
            [guideId]: {
              ...(prev[guideId] || {}),
              ...response.data,
              // Гарантируем, что completed имеет правильный тип
              completed: response.data.completed === true,
              guide: { _id: guideId },
            },
          };

          return updatedProgress;
        });

        return response.data;
      } catch (error) {
        console.error(
          `Ошибка при обновлении прогресса по гиду ${guideId}:`,
          error
        );
        return null;
      }
    },
    [isAuthenticated]
  );
  // Начало онбординга по выбранному гиду
  const startOnboarding = useCallback(
    async (guideId) => {
      if (!isAuthenticated) return;

      try {
        // Если гид не указан, берем первый доступный
        const targetGuideId =
          guideId ||
          (availableGuides.length > 0 ? availableGuides[0]._id : null);

        if (!targetGuideId) {
          console.error('Нет доступных гидов для онбординга');
          return;
        }

        console.log(`📱 Начало онбординга для гида ${targetGuideId}`);

        // Проверяем, активен ли уже онбординг с тем же гидом
        if (
          isOnboardingActive &&
          activeGuide &&
          activeGuide._id === targetGuideId
        ) {
          console.log(
            `⚠️ Онбординг для гида ${targetGuideId} уже активен, пропускаем повторный запуск`
          );
          return;
        }

        // Получаем гид и прогресс пользователя
        const guide = await onboardingAPI.getGuideById(targetGuideId);

        // Проверяем, что гид существует
        if (!guide.data) {
          console.error('Гид не найден');
          return;
        }

        // Получаем прогресс, только если он еще не загружен
        let progress = userProgress[targetGuideId];
        if (!progress) {
          progress = await fetchGuideProgress(targetGuideId);
        }

        // Устанавливаем активный гид и начальный шаг
        setActiveGuide(guide.data);
        setCurrentStep(progress?.currentStep || 0);
        setIsOnboardingActive(true);

        // Обновляем прогресс на сервере
        await updateProgress(targetGuideId, progress?.currentStep || 0);
      } catch (error) {
        console.error('Ошибка при запуске онбординга:', error);
      }
    },
    [
      isAuthenticated,
      availableGuides,
      activeGuide,
      isOnboardingActive,
      fetchGuideProgress,
      updateProgress,
      userProgress,
    ]
  );

  // Поиск следующего шага, учитывая условия отображения
  const findNextValidStep = useCallback(
    (guide, currentStepIndex) => {
      if (!guide || !guide.steps || guide.steps.length === 0) {
        return -1;
      }

      const totalSteps = guide.steps.length;
      let nextIndex = currentStepIndex + 1;

      // Если это последний шаг или нет следующего, возвращаем -1
      if (nextIndex >= totalSteps) {
        return -1;
      }

      // Проверяем следующие шаги, пока не найдем подходящий
      while (nextIndex < totalSteps) {
        const step = guide.steps[nextIndex];
        if (checkStepDisplayConditions(step)) {
          return nextIndex;
        }
        nextIndex++;
      }

      // Если не нашли подходящий шаг, возвращаем -1 (конец гайда)
      return -1;
    },
    [checkStepDisplayConditions]
  );

  // Поиск предыдущего шага, учитывая условия отображения
  const findPrevValidStep = useCallback(
    (guide, currentStepIndex) => {
      if (
        !guide ||
        !guide.steps ||
        guide.steps.length === 0 ||
        currentStepIndex <= 0
      ) {
        return -1;
      }

      let prevIndex = currentStepIndex - 1;

      // Проверяем предыдущие шаги, пока не найдем подходящий
      while (prevIndex >= 0) {
        const step = guide.steps[prevIndex];
        if (checkStepDisplayConditions(step)) {
          return prevIndex;
        }
        prevIndex--;
      }

      // Если не нашли подходящий шаг, возвращаем -1 (начало гайда)
      return -1;
    },
    [checkStepDisplayConditions]
  );

  // Метод для проверки завершенности гайда по ID - единый источник истины
  const isGuideCompleted = useCallback(
    (guideId) => {
      // Защита от вызова с неопределенным ID
      if (!guideId) {
        return false;
      }

      // Проверяем существование прогресса
      if (!userProgress || !userProgress[guideId]) {
        return false;
      }

      const progress = userProgress[guideId];
      
      // Дополнительная проверка на корректность данных прогресса
      if (!progress || typeof progress !== 'object') {
        return false;
      }

      // Проверяем флаг completed
      return progress.completed === true;
    },
    [userProgress]
  );

  // Проверка доступности гида по ролям
  const checkGuideRolesAccess = useCallback((guide, userRoles = []) => {
    // Если у гида не указаны роли, он доступен всем
    if (!guide || !guide.roles || guide.roles.length === 0) {
      return true;
    }

    // Если у пользователя нет ролей, но у гида они указаны - не доступен
    if (!userRoles || userRoles.length === 0) {
      return false;
    }

    // Проверяем пересечение ролей
    return userRoles.some((role) => guide.roles.includes(role));
  }, []);

  // Проверка соответствия гида текущему URL
  const checkGuideUrlMatch = useCallback((guide) => {
    // Проверяем только наличие гида и страницы
    return !!(guide && guide.page);
  }, []);

  // Комплексная проверка доступности гида для автозапуска
  const isGuideEligibleForAutostart = useCallback(
    (guide, userRoles = [], currentUrl = '') => {
      // Базовые проверки
      if (!guide || !guide.autostart) {
        return false;
      }

      const guideId = guide._id;

      // Проверка завершенности - незавершенные имеют приоритет
      if (isGuideCompleted(guideId)) {
        return false;
      }

      // Проверка ролей пользователя
      if (!checkGuideRolesAccess(guide, userRoles)) {
        return false;
      }

      // Проверка соответствия URL
      return checkGuideUrlMatch(guide);
    },
    [isGuideCompleted, checkGuideRolesAccess, checkGuideUrlMatch]
  );

  // Получение гида для автозапуска с четкой системой приоритетов
  const getAutostartGuide = useCallback(
    (guides = [], userRoles = []) => {
      if (!guides || !Array.isArray(guides) || guides.length === 0) {
        return null;
      }

      const currentUrl = window.location.pathname;

      // 1. Сначала ищем глобальные гиды (url: '*') - они имеют высший приоритет
      const eligibleGlobalGuide = guides.find(
        (guide) =>
          guide &&
          guide.page?.url === '*' &&
          guide.autostart &&
          !isGuideCompleted(guide._id) &&
          checkGuideRolesAccess(guide, userRoles)
      );

      if (eligibleGlobalGuide) {
        return eligibleGlobalGuide;
      }

      // 2. Затем ищем гиды для конкретной страницы
      const eligiblePageGuides = guides.filter((guide) =>
        isGuideEligibleForAutostart(guide, userRoles, currentUrl)
      );

      return eligiblePageGuides.length > 0 ? eligiblePageGuides[0] : null;
    },
    [isGuideCompleted, checkGuideRolesAccess, isGuideEligibleForAutostart]
  );

  // Получение шага для текущего маршрута
  const getCurrentRouteStep = useCallback(() => {
    if (!activeGuide || !location.pathname) return null;

    const currentStepData = activeGuide.steps[currentStep];
    if (!currentStepData) return null;

    // Проверяем, соответствует ли текущий маршрут маршруту этого шага
    if (
      currentStepData.route === '*' ||
      location.pathname.match(new RegExp(currentStepData.route))
    ) {
      return currentStepData;
    }

    return null;
  }, [activeGuide, currentStep, location.pathname]);

  // Проверка, должен ли быть активен онбординг на текущем маршруте
  const shouldShowOnboarding = useCallback(() => {
    if (!isOnboardingActive || !activeGuide) return false;

    const currentRouteStep = getCurrentRouteStep();
    return !!currentRouteStep && checkStepDisplayConditions(currentRouteStep);
  }, [
    isOnboardingActive,
    activeGuide,
    getCurrentRouteStep,
    checkStepDisplayConditions,
  ]);

  // Проверяем необходимость отображения онбординга при смене маршрута
  useEffect(() => {
    if (isOnboardingActive && activeGuide) {
      shouldShowOnboarding();
      // Можно добавить логику для перехода к следующему шагу,
      // если текущий не соответствует маршруту
    }
  }, [
    location.pathname,
    isOnboardingActive,
    activeGuide,
    shouldShowOnboarding,
  ]);

  // Загрузка данных о гайдах и прогрессе пользователя
  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      if (!isAuthenticated) return;

      try {
        setIsLoading(true);
        console.log('Начинаем загрузку данных для онбординга');

        // Запускаем оба запроса параллельно для оптимизации
        const [progressResponse, guidesResponse] = await Promise.all([
          // Загружаем прогресс пользователя
          onboardingAPI.getProgress().catch((err) => {
            console.error('Ошибка при загрузке прогресса:', err);
            return { data: [] };
          }),

          // Загружаем доступные гайды
          fetchAvailableGuides().catch((err) => {
            console.error('Ошибка при загрузке гайдов:', err);
            return [];
          }),
        ]);

        if (!isMounted) return;

        // Обработка данных прогресса
        if (progressResponse && progressResponse.data) {
          console.log(
            'Получен прогресс пользователя:',
            progressResponse.data.length,
            'записей'
          );

          const progressData = {};
          const completedGuideIds = [];

          progressResponse.data.forEach((item) => {
            if (item.guide && item.guide._id) {
              const isCompleted = item.completed === true;

              if (isCompleted) {
                completedGuideIds.push(item.guide._id);
              }

              progressData[item.guide._id] = {
                ...item,
                completed: isCompleted,
                guide: { _id: item.guide._id },
              };
            }
          });

          // Обновляем состояние прогресса
          setUserProgress(progressData);
          console.log(
            'Загружен прогресс пользователя. Завершенные гайды:',
            completedGuideIds
          );

          // Выводим детальную информацию о каждом завершенном гайде
          completedGuideIds.forEach((guideId) => {
            console.log(
              `Гайд ${guideId} отмечен как завершенный:`,
              progressData[guideId]
            );
          });
        }

        // Отмечаем что прогресс загружен (даже если данных нет)
        setIsProgressLoaded(true);
      } finally {
        if (isMounted) {
          setIsLoading(false);
          console.log('Загрузка данных завершена');
        }
      }
    };

    if (isAuthenticated) {
      loadData();
    } else {
      // Сбрасываем состояние при выходе пользователя
      setIsProgressLoaded(false);
      setUserProgress({});
    }

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, fetchAvailableGuides]);

  // Следим за изменением URL
  useEffect(() => {
    const currentPathname = location.pathname;
    if (currentPathname !== currentUrl) {
      // Если URL изменился, получаем новые гиды
      fetchAvailableGuides();
    }
  }, [location.pathname, currentUrl]);

  // Завершение текущего онбординга
  const completeOnboarding = useCallback(
    async (guideId) => {
      if (!isAuthenticated) {
        return { success: false, error: 'Пользователь не аутентифицирован' };
      }

      try {
        const targetGuideId = guideId || activeGuide?._id;

        if (!targetGuideId) {
          return { success: false, error: 'Не указан ID гида' };
        }

        console.log(`Завершение гида: ${targetGuideId}`);

        // Обновляем состояние перед запросом к серверу
        setIsOnboardingActive(false);

        // Отправляем запрос на сервер
        const response = await onboardingAPI.completeGuide(targetGuideId);

        if (!response.data) {
          return { success: false, error: 'Ошибка сервера' };
        }

        // Обновляем прогресс в состоянии с явной установкой флага completed
        setUserProgress((prev) => ({
          ...prev,
          [targetGuideId]: {
            ...(prev[targetGuideId] || {}),
            ...response.data.progress,
            completed: true,
            guide: { _id: targetGuideId },
          },
        }));

        setActiveGuide(null);

        return { success: true, data: response.data };
      } catch (error) {
        console.error('Ошибка при завершении онбординга:', error);
        return { success: false, error: error.message || 'Неизвестная ошибка' };
      }
    },
    [isAuthenticated, activeGuide]
  );

  // Перезапуск онбординга
  const restartOnboarding = useCallback(
    async (guideId) => {
      if (!isAuthenticated) return;

      try {
        const targetGuideId = guideId || activeGuide?._id;

        if (!targetGuideId) {
          console.error('Не указан ID гида для перезапуска');
          return;
        }

        console.log(`⚙️ Перезапуск гида ${targetGuideId}`);

        // Сначала убедимся, что гид отмечен как незавершенный в нашем состоянии
        setUserProgress((prev) => {
          return {
            ...prev,
            [targetGuideId]: {
              ...(prev[targetGuideId] || {}),
              currentStep: 0,
              completed: false,
              guide: { _id: targetGuideId },
            },
          };
        });

        // Отправляем запрос на сервер о перезапуске гида
        const response = await onboardingAPI.restartGuide(targetGuideId);
        console.log(`✅ Ответ сервера о перезапуске:`, response.data);

        // Запускаем онбординг заново
        await startOnboarding(targetGuideId);

        return response.data;
      } catch (error) {
        console.error('Ошибка при перезапуске онбординга:', error);
        return null;
      }
    },
    [isAuthenticated, activeGuide, startOnboarding]
  );

  // Вспомогательная функция для извлечения идентификатора вкладки из селектора
  const getTabIdFromSelector = useCallback((selector) => {
    if (!selector) return null;

    // Извлекаем data-tab-id из селектора
    const dataTabIdMatch = selector.match(/\[data-tab-id="([^"]+)"\]/);
    if (dataTabIdMatch && dataTabIdMatch[1]) {
      return dataTabIdMatch[1];
    }

    // Для обратной совместимости проверяем nth-child
    const nthChildMatch = selector.match(/nth-child\((\d+)\)/);
    if (nthChildMatch && nthChildMatch[1]) {
      return parseInt(nthChildMatch[1]) - 1;
    }

    return null;
  }, []);

  // Обновляем значения для провайдера
  const value = {
    isOnboardingActive,
    currentStep,
    totalSteps: activeGuide?.steps?.length || 0,
    isLoading,
    isProgressLoaded,
    activeGuide,
    availableGuides,
    setAvailableGuides,
    userProgress,
    startOnboarding,
    restartOnboarding,
    completeOnboarding,
    fetchGuideProgress,
    getCurrentRouteStep,
    shouldShowOnboarding,
    fetchGuidesByTab,
    fetchAvailableGuides,
    currentUrl,
    currentTabId,
    setCurrentTabId,
    // Методы для работы с гидами
    checkStepDisplayConditions,
    findNextValidStep,
    findPrevValidStep,
    isGuideCompleted,
    isGuideEligibleForAutostart,
    getAutostartGuide,
    checkGuideRolesAccess,
    checkGuideUrlMatch,
    // Вспомогательные функции
    getTabIdFromSelector,
    pageRef,
    setPageRef,
    shouldOpenDrawer,
    handleOnboardingDrawer,
    checkStepUIRequirements,
    setLayoutRef,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

export default OnboardingContext;
